import { useEffect, useRef } from 'react';
import { Modal } from 'react-bootstrap';
import { formatPhoneForDisplay } from '../../utils/phoneFormat';
import { shareReceiptText } from '../../utils/shareReceipt';

// Matches CounterpartyReveal so the two modals hide themselves on the
// same schedule. A minute is long enough to read a receipt aloud but
// short enough that a phone left face up on a table does not keep the
// source phone number visible.
const AUTO_CLOSE_MS = 60_000;

const badgeLabelClass = 'badge bg-light text-primary text-uppercase fw-semibold';

const formatAmount = (amount) =>
    amount != null
        ? `KES ${Number(amount).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`
        : null;

const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleString('en-KE', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
    });
};

const statusDescriptor = (status) => {
    switch (status) {
        case 'successful':   return { cls: 'text-success',   bg: 'bg-success-subtle',   icon: 'fa-check-circle',    label: 'Successful' };
        case 'under_review': return { cls: 'text-info',      bg: 'bg-info-subtle',      icon: 'fa-search',          label: 'Under Review' };
        case 'pending':      return { cls: 'text-warning',   bg: 'bg-warning-subtle',   icon: 'fa-hourglass-half',  label: 'Pending' };
        case 'failed':       return { cls: 'text-danger',    bg: 'bg-danger-subtle',    icon: 'fa-times-circle',    label: 'Failed' };
        default:             return { cls: 'text-secondary', bg: 'bg-secondary-subtle', icon: 'fa-question-circle', label: 'Unknown' };
    }
};

const TopUpReveal = ({
    topUp,
    isOpen: isOpenProp,
    onOpenChange,
    hideEye = false,
}) => {
    const isControlled = typeof isOpenProp === 'boolean';
    const isOpen = isControlled ? isOpenProp : false;
    const autoCloseTimer = useRef(null);

    const clearAutoClose = () => {
        if (autoCloseTimer.current) {
            clearTimeout(autoCloseTimer.current);
            autoCloseTimer.current = null;
        }
    };

    const setOpen = (next) => {
        if (onOpenChange) onOpenChange(next);
    };

    const handleOpen = (event) => {
        if (event) event.stopPropagation();
        setOpen(true);
    };

    const handleClose = () => {
        clearAutoClose();
        setOpen(false);
    };

    useEffect(() => {
        if (!isOpen) {
            clearAutoClose();
            return;
        }
        clearAutoClose();
        autoCloseTimer.current = setTimeout(() => handleClose(), AUTO_CLOSE_MS);
        return clearAutoClose;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, topUp?.id]);

    const status = statusDescriptor(topUp?.status);
    const formattedPhone = formatPhoneForDisplay(topUp?.source_phone_number);
    const capturedAt = topUp?.post_mpesa_transaction_date ?? topUp?.created_at;

    return (
        <>
            {!hideEye && (
                <button
                    type="button"
                    className="topup-reveal-eye btn btn-link p-0 text-primary d-inline-flex align-items-center gap-1 text-decoration-none"
                    onClick={handleOpen}
                    aria-label="Show top-up details"
                    title="Show top-up details"
                    style={{ fontSize: '0.85rem', lineHeight: 1 }}
                >
                    <i className="fa fa-eye" aria-hidden="true" />
                    <span className="fw-medium">View</span>
                </button>
            )}
            {/* React synthetic events bubble from a portaled Modal through
                the React component tree back to the clickable row, so this
                shield stops a close click from re-opening the modal. */}
            <span onClick={(event) => event.stopPropagation()} onKeyDown={(event) => event.stopPropagation()}>
            <Modal
                show={isOpen}
                onHide={handleClose}
                centered
            >
                <Modal.Header closeButton>
                    <Modal.Title className="fs-6 text-muted">Top-up details</Modal.Title>
                </Modal.Header>
                <Modal.Body className="text-center py-4">
                    <div className="d-flex flex-column align-items-center mb-3" style={{ gap: '2px' }}>
                        <span
                            className={`d-inline-flex align-items-center justify-content-center rounded-circle ${status.bg} ${status.cls}`}
                            style={{ width: '72px', height: '72px', fontSize: '2rem' }}
                        >
                            <i className={`fa ${status.icon}`} aria-hidden="true" />
                        </span>
                        <span className={`fs-5 fw-semibold ${status.cls}`}>{status.label}</span>
                    </div>
                    <div className="mb-3">
                        <span className={badgeLabelClass}>M-Pesa Top-up</span>
                    </div>
                    {formatAmount(topUp?.amount) ? (
                        <div className="fs-2 fw-bold text-primary mb-3">
                            {formatAmount(topUp.amount)}
                        </div>
                    ) : (
                        <div className="fs-4 fst-italic text-muted mb-3">Amount pending</div>
                    )}
                    {formattedPhone && (
                        <div className="fs-5 mb-3 d-flex align-items-center justify-content-center gap-2 flex-wrap">
                            <span className={badgeLabelClass}>From</span>
                            <span className="text-body">{formattedPhone}</span>
                        </div>
                    )}
                    {topUp?.post_mpesa_receipt_number && (
                        <div className="mb-3">
                            <div className="small text-muted mb-1">Receipt</div>
                            <div
                                className="fs-2 fw-bold text-primary"
                                style={{ fontFamily: 'Menlo, Consolas, "Liberation Mono", monospace', letterSpacing: '0.22em', wordBreak: 'break-word' }}
                            >
                                {topUp.post_mpesa_receipt_number}
                            </div>
                        </div>
                    )}
                    {capturedAt && (
                        <div className="mt-3 pt-3 border-top">
                            <div className="small text-uppercase text-muted mb-1" style={{ letterSpacing: '0.06em' }}>Received at</div>
                            <div className="text-body">{formatDate(capturedAt)}</div>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer className="justify-content-center border-0 pt-0 flex-column gap-2">
                    <button
                        type="button"
                        className="btn btn-outline-primary btn-sm px-4 rounded-pill"
                        onClick={() => {
                            const lines = [
                                'Milipot receipt',
                                '',
                                'M-Pesa Top-up',
                            ];
                            if (formattedPhone) lines.push(`From ${formattedPhone}`);
                            if (formatAmount(topUp?.amount)) lines.push(formatAmount(topUp.amount));
                            if (topUp?.post_mpesa_receipt_number) lines.push(`Receipt: ${topUp.post_mpesa_receipt_number}`);
                            lines.push(`Status: ${status.label}`);
                            if (capturedAt) lines.push(`Received at ${formatDate(capturedAt)}`);
                            shareReceiptText(lines.join('\n'), 'Milipot top-up receipt');
                        }}
                    >
                        <i className="bi bi-share-fill me-2"></i>
                        Share receipt
                    </button>
                    <small className="text-muted fst-italic">Hides automatically after 60 seconds.</small>
                </Modal.Footer>
            </Modal>
            </span>
        </>
    );
};

export default TopUpReveal;
